import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Microphone, Headphones, PencilSimple, Certificate } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs, localBusiness } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { NotifyMe } from "@/components/NotifyMe";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALT = {
  reading: "A notebook of English words and their Latin roots open on a desk beside a cup of tea",
  writing: "An essay page marked up in pencil with clearer wording written in the margin",
  speaking: "A young adult practising spoken English aloud at a kitchen table",
  listening: "Headphones resting on an open notebook beside an answer sheet",
};

export default function EnglishPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/english/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ sections: [] }));
  }, []);

  const word = board?.word || {};

  useSeo({
    title: "Learn English by the root — IELTS band 7+ and TOEFL practice marked by AI | Unity & Utility",
    description: "Reading, writing, speaking and listening for young adults anywhere in the world. Every lesson starts from a Greek or Latin root and builds the whole word family, with picture cards, a free week, then $15 a month. Writing and speaking marked against the IELTS bands and the TOEFL scale, a tutor you can talk to, levels and certificates.",
    path: "/english",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbs([["Home", "/"], ["English course", "/english"]]),
        localBusiness({
          name: "Unity & Utility English course",
          description: "AI-marked English course for IELTS band 7+ and TOEFL: reading, writing, speaking, listening and word building from Greek and Latin roots.",
          path: "/english",
          image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
          type: "EducationalOrganization",
          area: "Worldwide",
        }),
      ],
    },
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="english-public-page">
        <Crumbs items={[["Home", "/"], ["English course", "/english"]]} />
        <p className="overline text-[#B3241B]">English, by the root</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <BookOpen size={34} weight="duotone" className="mt-1 shrink-0 text-[#B3241B]" />
          Word power, the way it sticks
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="english-public-intro">
          Every lesson starts from one Greek or Latin root and builds the whole family of words
          that grow out of it — so you are not memorising lists, you are recognising words you
          have never met before. Written plainly for young adults anywhere in the world, with a
          picture for every word, your own country and first language taken into account, and
          reading, writing, speaking and listening all marked against the IELTS bands and the
          TOEFL scale. Aimed squarely at band 7 and above. {board?.trial_days || 7} days free,
          then {board?.plans?.monthly?.label || "$15 a month"} — or{" "}
          {board?.plans?.yearly?.label || "$150 a year"}.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link to="/english-desk" data-testid="english-public-start"
            className="tap-safe inline-flex items-center bg-[#B3241B] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#8E1C15]">
            Start your free week
          </Link>
          <ShareBar url={board?.share_token
            ? `${window.location.origin}/p/${board.share_token}`
            : `${window.location.origin}/english`}
            text="Learn English by the root — IELTS band 7+ practice marked by AI."
            testid="english-public-share" instagram compact />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`} alt={ALT.reading} loading="lazy"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="english-public-image" />
        )}

        {word.word && (
          <section className="mt-6 border border-[#B3241B] bg-[#FFF3F2] p-5" data-testid="english-public-word">
            <p className="overline text-[#B3241B]">
              Word of the day{board?.day ? ` · ${board.day}` : ""}
              {board?.auto_posted && <span className="ml-2">Posted to Facebook</span>}
            </p>
            <h2 className="font-display mt-1 text-2xl font-bold text-[#0F172A] md:text-3xl">
              {word.word}
            </h2>
            <p className="mt-1 text-sm font-semibold text-slate-500">
              from <span className="italic">{word.root}</span> — {word.origin}
            </p>
            <p className="mt-2 text-base leading-relaxed text-slate-700">{word.meaning}</p>
            {word.sentence && (
              <p className="mt-2 border-l-2 border-[#B3241B] pl-3 text-base italic text-slate-600">
                {word.sentence}
              </p>
            )}
            {word.family?.length > 0 && (
              <p className="mt-3 text-sm text-slate-600">
                Same family: {word.family.join(" · ")}
              </p>
            )}
            {word.exam_note && (
              <p className="mt-2 text-sm font-semibold text-[#B3241B]">{word.exam_note}</p>
            )}
          </section>
        )}

        {board?.cards?.length > 0 && (
          <section className="mt-10" data-testid="english-public-cards">
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              Study pictures — read the word off the scene
            </h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.cards.map((c, i) => (
                <figure key={c.word} className="border border-border bg-white"
                  data-testid={`english-public-card-${i}`}>
                  <img src={`${BACKEND}${c.image_url}`} alt={`Picture card for ${c.word}`}
                    loading="lazy" className="h-40 w-full object-cover" />
                  <figcaption className="p-3 font-display text-base font-bold text-[#0F172A]">
                    {c.word}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10 grid gap-3 sm:grid-cols-2" data-testid="english-public-skills">
          {[["reading", BookOpen, "Reading and word building",
             "A root, its whole word family, a passage written for your level and a self-test that remembers what you got wrong."],
            ["writing", PencilSimple, "Writing, marked and mended",
             "Write the task, get a band for each criterion, your own sentences rewritten better, and a model answer one band above you."],
            ["speaking", Microphone, "Speaking, recorded and scored",
             "Record your answer. It is transcribed, scored for fluency, grammar and pronunciation, and the words you blurred are spelled out to say."],
            ["listening", Headphones, "Listening practice",
             "An original talk or conversation read aloud, then questions on detail, gist and inference — with the text hidden."]].map(
            ([key, Icon, title, text], i) => (
              <div key={key} className="border border-border bg-white" data-testid={`english-public-skill-${i}`}>
                {board?.section_images?.[key] && (
                  <img src={`${BACKEND}${board.section_images[key]}`} alt={ALT[key] || title}
                    loading="lazy" className="h-40 w-full object-cover"
                    data-testid={`english-public-image-${key}`} />
                )}
                <div className="p-4">
                  <h3 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A]">
                    <Icon size={18} weight="duotone" className="text-[#B3241B]" /> {title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{text}</p>
                </div>
              </div>
            ))}
        </section>

        <section className="mt-10" data-testid="english-public-roots">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Roots in the course — {board?.roots_in_course || 20} of them, one at a time
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {(board?.roots || []).map((r, i) => (
              <div key={r.root} className="border border-border bg-white p-4"
                data-testid={`english-public-root-${i}`}>
                <p className="font-display text-base font-bold text-[#0F172A]">
                  <span className="italic">{r.root}</span>
                  <span className="ml-2 text-sm font-normal text-slate-500">{r.origin}</span>
                </p>
                <p className="mt-1 text-sm text-slate-600">{r.family}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 border-l-2 border-[#B3241B] bg-[#FFF3F2] px-4 py-4" data-testid="english-public-how">
          <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
            <Certificate size={18} weight="duotone" className="text-[#B3241B]" />
            What you get
          </h2>
          <ul className="mt-2 space-y-1.5 text-base leading-relaxed text-slate-700">
            <li>A free week, then {board?.plans?.monthly?.label || "$15 a month"} — cancel any time.</li>
            <li>Or {board?.plans?.yearly?.label || "$150 a year"} if you are in it for the long run.</li>
            <li>An employer can buy seats, and their staff learn free.</li>
            <li>A tutor you can simply talk to, which corrects you as you go.</li>
            <li>Levels from {board?.levels?.[0] || "Foundation"} to {board?.levels?.slice(-1)[0] || "Band 8"}, with a certificate at each one.</li>
            <li>A daily streak, so it becomes a habit rather than a cram.</li>
          </ul>
          <p className="mt-3 text-sm text-slate-500">
            Our own original lessons, written in the root-and-family tradition that Norman Lewis
            made famous — not a copy of his book or anybody else's.
          </p>
          <Link to="/english-desk" data-testid="english-public-open-desk"
            className="tap-safe mt-3 inline-flex items-center text-sm font-semibold text-[#B3241B] underline">
            Open the English desk
          </Link>
        </section>

        <div className="mt-10">
          <NotifyMe source="english" testid="english-public-notify"
            blurb="New roots, picture cards and exam practice go up every week. Leave your email and we will tell you what is new and when your exam practice is ready." />
        </div>
      </div>
    </PublicShell>
  );
}
